import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    InputAdornment,
    Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import {
    EntityTargetScoreAssignment,
    useGetAllEntityTargetScoreAssignments,
    useDeleteEntityTargetScoreAssignment,
} from "src/api/client-api/entityTargetScoreAssignment/useEntityTargetScoreAssignment";
import { Loading } from "src/components/utils/Loading";
import { CREATE, ENTITY_TARGET_SCORE_ASSIGNMENT, useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { useQueryClient } from "react-query";
import { useSnackbar } from "src/utils/useSnackbar";
import { EntityTargetScoreAssignmentModal } from "./EntityTargetScoreAssignmentModal";
import { SettingsError } from "./SettingsError";
import { useNavigateSettingsParent } from "./useNavigateSettingsParent";

export const EntityTargetScoreAssignmentPage = () => {
    const { canUser } = useFoodpilotContext();
    const [searchTerm, setSearchTerm] = useState("");

    const { assignments, isLoading, isError } = useGetAllEntityTargetScoreAssignments();
    const deleteMutation = useDeleteEntityTargetScoreAssignment();
    const navigateSettingsParent = useNavigateSettingsParent();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();

    const [displayDialog, setDisplayDialog] = useState(false);
    const [idToDelete, setIdToDelete] = useState<number | undefined>(undefined);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState<EntityTargetScoreAssignment | undefined>(undefined);

    if (isLoading) return <Loading />;

    if (isError) {
        return (
            <SettingsError
                heading={t("settings.score.entity_target_score_assignments.title", "Entity target score assignments")}
                message={t("settings.score.entity_target_score_assignments.title", "Entity target score assignments")}
            />
        );
    }

    const handleDeleteClick = (id: number) => {
        setIdToDelete(id);
        setDisplayDialog(true);
    };

    const handleOpenCreateModal = () => {
        setEditing(undefined);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (row: EntityTargetScoreAssignment) => {
        setEditing(row);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditing(undefined);
    };

    const handleModalSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ["getAllEntityTargetScoreAssignments"] });
        snackbar.setSnackbarProps({
            message: editing ? "Item updated successfully" : "Item created successfully",
            type: "success",
        });
    };

    const filtered = (assignments || []).filter((it) => {
        const term = searchTerm.toLowerCase();
        return (
            it.label.toLowerCase().includes(term) ||
            it.identifier.toLowerCase().includes(term) ||
            (it.description ? it.description.toLowerCase().includes(term) : false)
        );
    });

    const renderConfirmDialog = () => (
        <Dialog
            open={displayDialog}
            onClose={() => setDisplayDialog(false)}
            PaperProps={{
                sx: { borderRadius: "16px", padding: "32px", width: "400px" },
            }}
        >
            <Box sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", mb: 2 }}>
                    {t("settings.score.entity_target_score_assignments.delete.confirm.title", "Confirm Deletion")}
                </Typography>
                <Typography variant="small" sx={{ lineHeight: "1.6em", color: "#393A36", mb: 3 }}>
                    {t(
                        "settings.score.entity_target_score_assignments.delete.confirm.message",
                        "Are you sure you want to delete this item?",
                    )}
                </Typography>
                <Box sx={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                    <Button
                        variant="outlined"
                        onClick={() => setDisplayDialog(false)}
                        sx={{
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            borderColor: "#E7E6E4",
                            color: "#393A36",
                        }}
                    >
                        {t("cancel", "Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (idToDelete) {
                                deleteMutation.mutate(idToDelete, {
                                    onSuccess: () => {
                                        queryClient.invalidateQueries({
                                            queryKey: ["getAllEntityTargetScoreAssignments"],
                                        });
                                        snackbar.setSnackbarProps({
                                            message: "Item deleted successfully",
                                            type: "success",
                                        });
                                        setDisplayDialog(false);
                                        setIdToDelete(undefined);
                                    },
                                    onError: () => {
                                        snackbar.setSnackbarProps({
                                            message: "Failed to delete item. Please try again.",
                                            type: "error",
                                        });
                                    },
                                });
                            }
                        }}
                        sx={{
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            background: "linear-gradient(180deg, #FF6B6B 0%, #FF5252 100%)",
                            color: "white",
                        }}
                    >
                        {t("delete", "Delete")}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );

    const getEntityDisplayName = (entityClass: string) => {
        switch (entityClass) {
            case "App\\Entity\\Product":
                return t("entity.product", "Product");
            case "App\\Entity\\ProductLine":
                return t("entity.product_line", "Product line");
            case "App\\Entity\\Ingredient":
                return t("entity.ingredient", "Ingredient");
            case "App\\Core\\Tenancy\\Entity\\Organization":
            case "App\\Entity\\Company":
                return t("entity.company", "Company");
            case "App\\Core\\Tenancy\\Entity\\OrganizationSector":
            case "App\\Entity\\CompanySector":
                return t("entity.company_sector", "Company sector");
            default:
                return entityClass;
        }
    };

    return (
        <Box sx={{ padding: "32px" }}>
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={navigateSettingsParent}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Box sx={{ marginLeft: "16px" }}>
                        <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#1E1E1E" }}>
                            {t(
                                "settings.score.entity_target_score_assignments.title",
                                "Entity target score assignments",
                            )}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ padding: "32px", backgroundColor: "#FEFCFB" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                        <TextField
                            placeholder={t(
                                "settings.score.entity_target_score_assignments.search.placeholder",
                                "Search...",
                            )}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{
                                width: "300px",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "44px",
                                    backgroundColor: "white",
                                    height: "40px",
                                    border: "1px solid #E7E6E4",
                                    "& fieldset": { border: "none" },
                                    "&:hover fieldset": { border: "none" },
                                    "&.Mui-focused fieldset": { border: "none" },
                                },
                                "& .MuiInputBase-input": {
                                    fontSize: "0.875rem",
                                    fontWeight: 400,
                                    lineHeight: "1.6em",
                                    color: "#393A36",
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "#A3A2A0", fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {canUser(CREATE, ENTITY_TARGET_SCORE_ASSIGNMENT) && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                                onClick={handleOpenCreateModal}
                                sx={(theme) => ({
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    borderRadius: "44px",
                                    px: 2.5,
                                    py: 1.25,
                                    height: "40px",
                                    textTransform: "none",
                                    fontSize: "0.875rem",
                                    fontWeight: 700,
                                    color: "white",
                                    boxShadow: "0px 4px 8px 0px rgba(42, 43, 39, 0.08)",
                                    "&:hover": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                        boxShadow: "0px 6px 12px 0px rgba(42, 43, 39, 0.12)",
                                    },
                                })}
                            >
                                {t(
                                    "settings.score.entity_target_score_assignments.add",
                                    "Add a new target score assignment",
                                )}
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Content area */}
                <Box sx={{ padding: "0px 32px 24px" }}>
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            border: "1px solid #E7E6E4",
                            borderRadius: "8px",
                            overflow: "hidden",
                        }}
                    >
                        <Table sx={{ "& .MuiTableCell-root": { border: "none" } }}>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: "#FEFCFB",
                                        "& .MuiTableCell-root": {
                                            borderBottom: "1px solid #E7E6E4",
                                            borderRight: "1px solid #E7E6E4",
                                            padding: "0px 14px 0px 20px",
                                            height: "48px",
                                            "&:last-child": {
                                                borderRight: "none",
                                            },
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("settings.score.entity_target_score_assignments.table.label", "Label")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t(
                                                "settings.score.entity_target_score_assignments.table.identifier",
                                                "Identifier",
                                            )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t(
                                                "settings.score.entity_target_score_assignments.table.appliesTo",
                                                "Applies To",
                                            )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("settings.score.entity_target_score_assignments.table.value", "Value")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t(
                                                "settings.score.entity_target_score_assignments.table.description",
                                                "Description",
                                            )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t(
                                                "settings.score.entity_target_score_assignments.table.score_id",
                                                "Score ID",
                                            )}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("actions", "Actions")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.length === 0 ?
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                                            <Typography variant="small" sx={{ color: "#A3A2A0" }}>
                                                {searchTerm ?
                                                    t(
                                                        "settings.score.entity_target_score_assignments.table.noResults",
                                                        "No items match your search.",
                                                    )
                                                :   t(
                                                        "settings.score.entity_target_score_assignments.table.empty",
                                                        "No items defined yet.",
                                                    )
                                                }
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                :   filtered.map((row, index) => (
                                        <TableRow
                                            key={row.id}
                                            sx={{
                                                "& .MuiTableCell-root": {
                                                    borderRight: "1px solid #E7E6E4",
                                                    padding: "0px 14px 0px 20px",
                                                    height: "58px",
                                                    "&:last-child": {
                                                        borderRight: "none",
                                                        padding: "0px 8px",
                                                    },
                                                },
                                                "&:hover": {
                                                    backgroundColor: "#F9F9F8",
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "22px", color: "#1E1E1E", fontWeight: 600 }}
                                                >
                                                    {row.label}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "1.6em", color: "#393A36" }}
                                                >
                                                    {row.identifier}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "1.6em", color: "#393A36" }}
                                                >
                                                    {getEntityDisplayName(row.entityClass)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "1.6em", color: "#393A36" }}
                                                >
                                                    {row.value != null && row.value !== 0 ?
                                                        row.value
                                                    :   t("common.coming_soon", "Coming Soon")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                dangerouslySetInnerHTML={{ __html: row.description ?? "" }}
                                                sx={(theme) => ({
                                                    borderBottom: "none",
                                                    color: "#393A36",
                                                    "* > a": { color: theme.palette.primary.main },
                                                })}
                                            />
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "1.6em", color: "#393A36" }}
                                                >
                                                    {row.scoreId ?? ""}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    {row.canModify && (
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleOpenEditModal(row)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                        >
                                                            <FoodsIcon icon="edit" size={1} />
                                                        </Box>
                                                    )}
                                                    {row.canDelete && (
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleDeleteClick(row.id)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                        >
                                                            <FoodsIcon icon="delete" size={1} />
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            {renderConfirmDialog()}

            <EntityTargetScoreAssignmentModal
                open={isModalOpen}
                onClose={handleCloseModal}
                assignment={editing}
                onSuccess={handleModalSuccess}
            />
        </Box>
    );
};

export default EntityTargetScoreAssignmentPage;
